/**
 * ThemeSwitch — light or dark, one click from the header (themebtn).
 *
 * Founder, 3 Nov 2026: the theme lived only in Settings → Preferences, behind a
 * tab that sat past the edge of the tab strip. The header frame is frozen
 * (PLAIN_FACE_SPEC §0, §9); the founder lifted it for this one button.
 *
 * - ThemeButton: the header icon, laptop only (the header has no room on a
 *   phone). It flips light ⇄ dark and shows where a click takes you: a moon
 *   while the screen is light, a sun while it is dark. "System" stays in
 *   Settings → Preferences.
 * - ThemeChoice: Light · Dark · System, for the avatar menu on a phone.
 *
 * The choice is the person's on this device (`karr_theme`, read before first
 * paint by index.html), never the business's: a CA moving between twenty
 * clients' books keeps one screen.
 *
 * @since themebtn (2026-11-03)
 */
import { Moon, Sun, Monitor } from 'lucide-react';
import { setTheme } from '../utils/theme';
import { useDarkNow, useThemePref } from '../hooks/useThemePref';

export function ThemeButton({ className = '' }: { className?: string }) {
    const dark = useDarkNow();
    const label = dark ? 'Light screen' : 'Dark screen';
    return (
        <button
            type="button"
            id="btn-theme"
            onClick={() => setTheme(dark ? 'light' : 'dark')}
            className={`items-center justify-center w-8 h-8 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 dark:hover:text-stone-300 dark:hover:bg-stone-800 ${className}`}
            aria-label={label}
            title={label}
        >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
    );
}

const CHOICES = [
    { id: 'light', label: 'Light', icon: Sun },
    { id: 'dark', label: 'Dark', icon: Moon },
    { id: 'system', label: 'System', icon: Monitor },
] as const;

export function ThemeChoice() {
    const pref = useThemePref();
    return (
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Screen">
            {CHOICES.map(({ id, label, icon: Icon }) => (
                <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={pref === id}
                    onClick={() => setTheme(id)}
                    className={`flex flex-1 items-center justify-center gap-1.5 min-h-10 px-2.5 rounded-md border text-sm ${pref === id
                        ? 'border-teal-600 text-teal-700 dark:border-teal-500 dark:text-teal-300'
                        : 'border-stone-200 text-stone-600 hover:border-stone-300 dark:border-stone-700 dark:text-stone-300'
                        }`}
                >
                    <Icon size={14} />
                    {label}
                </button>
            ))}
        </div>
    );
}
